import { useEffect, useRef, useState } from "react";
import { LineStyle } from "lightweight-charts";
import { api } from "../lib/api";

const GHOST = { upColor: "rgba(16,185,129,0.28)", downColor: "rgba(239,68,68,0.28)", borderVisible: true, borderUpColor: "rgba(16,185,129,0.7)", borderDownColor: "rgba(239,68,68,0.7)", wickUpColor: "rgba(16,185,129,0.5)", wickDownColor: "rgba(239,68,68,0.5)", priceLineVisible: false, lastValueVisible: false };
const BAND = { color: "#94A3B8", lineWidth: 1, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false };

export function useForecast(chart, enabled, symbol, tf, ready) {
  const series = useRef([]);
  const [fc, setFc] = useState(null);
  const clear = () => { series.current.forEach((s) => { try { chart.current?.removeSeries(s); } catch (e) { /* */ } }); series.current = []; };
  useEffect(() => {
    if (!enabled || !ready) { clear(); setFc(null); return undefined; }
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/market/forecast-candles", { params: { symbol, tf } });
        if (!alive || !chart.current) return;
        clear();
        setFc(r.data);
        if (!r.data.available) return;
        const c = r.data.candles;
        const g = chart.current.addCandlestickSeries(GHOST);
        g.setData(c.map((x) => ({ time: x.time, open: x.open, high: x.high, low: x.low, close: x.close })));
        const start = { time: r.data.base_time, value: r.data.base_close };
        const hi = chart.current.addLineSeries(BAND); hi.setData([start, ...c.map((x) => ({ time: x.time, value: x.p95 }))]);
        const lo = chart.current.addLineSeries(BAND); lo.setData([start, ...c.map((x) => ({ time: x.time, value: x.p5 }))]);
        series.current = [g, hi, lo];
      } catch (e) { /* keep last */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); clear(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, symbol, tf, ready]);
  return fc;
}

export const ForecastBadge = ({ fc, id }) => {
  if (!fc) return <span className="mono animate-pulse text-[9px] text-slate-400" data-testid={`forecast-loading-${id}`}>Predicting 10 candles…</span>;
  if (!fc.available) return <span className="mono text-[9px] text-slate-400" data-testid={`forecast-na-${id}`}>Predict: {fc.reason}</span>;
  const a = fc.accuracy || {};
  const last = fc.candles[fc.candles.length - 1];
  const beats = a.direction_hit_pct != null && a.always_up_pct != null && a.direction_hit_pct > Math.max(a.always_up_pct, 100 - a.always_up_pct);
  return (
    <span className="mono rounded-sm bg-blue-500/10 px-1 text-[9px] text-blue-700" data-testid={`forecast-badge-${id}`} title={fc.note}>
      Predict 10: {last.close > fc.base_close ? "▲" : "▼"} {last.close} · P(up) {Math.round(last.prob_up * 100)}%{last.edge ? "" : " (no edge)"} ·
      {" "}accuracy <b data-testid={`forecast-acc-${id}`}>{a.direction_hit_pct ?? "—"}%</b> of {a.evaluated ?? 0}
      {a.always_up_pct != null ? ` (always-up ${a.always_up_pct}%${beats ? "" : ", not better"})` : ""} · band {a.band_coverage_pct ?? "—"}%
    </span>
  );
};
